import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getOptionalCourses, setOptionalCourses } from 'Api/axios/optional-courses-api';

/**
 * Carga el catálogo de cursos y guarda la selección de opcionales.
 *
 * Cada curso viaja como objeto `{ id, name, duration, categoryName,
 * optionalForAll }`. La terminal no aplica a los opcionales, así que no viaja.
 *
 * Ni getOptionalCourses ni setOptionalCourses rechazan: ambas resuelven con
 * `{ error }`. Por eso el error se lee del resultado, no de un `.catch`.
 *
 * @param {Function} setIsLoading - Apaga el spinner de la tabla.
 */
const useCourse = setIsLoading => {
  const history = useHistory();
  const [courses, setCourses] = useState([]);
  const [defaultSelectedIds, setDefaultSelectedIds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    getOptionalCourses().then(({ data, error }) => {
      if (!isMounted) return;

      if (error !== null) {
        // Una lista vacía por error es indistinguible de "no hay cursos": hay
        // que decirlo, o el admin cree que el catálogo está vacío.
        setLoadError(true);
        setIsLoading(false);
        return;
      }

      // Marcado solo lo que es opcional para todos. Un curso opcional por
      // invitación aparece en la lista pero desmarcado: si apareciera marcado,
      // cualquier guardado de esta pantalla lo ensancharía a todos los
      // empleados sin que nadie lo pidiera.
      const rows = data.map(course => ({
        id: course.id,
        name: course.name || '',
        duration: course.duration || 0,
        categoryName: course.categoryName || 'sin categoría',
        optionalForAll: Boolean(course.isOptional && course.optionalScope === 'all'),
      }));

      setCourses(rows);
      setCategories([...new Set(rows.map(row => row.categoryName))]);
      setDefaultSelectedIds(rows.filter(row => row.optionalForAll).map(row => row.id));
      setIsLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * Guarda la selección. Solo redirige si el guardado se confirmó: la pantalla
   * de destino muestra "Cambios realizados con éxito" al recibir el state, así
   * que redirigir tras un fallo le miente al administrador.
   *
   * @param {{id: string, value: boolean}[]} courseData - Cursos a marcar o desmarcar.
   */
  const saveCourses = courseData => {
    setSaveError(false);

    setOptionalCourses(courseData).then(({ error }) => {
      if (error !== null) {
        setSaveError(true);
        return;
      }

      history.push({
        pathname: '/definicion',
        state: {
          update: true,
        },
      });
    });
  };

  return {
    courses,
    setCourses,
    categories,
    setCategories,
    saveCourses,
    defaultSelectedIds,
    loadError,
    setLoadError,
    saveError,
    setSaveError,
  };
};

export default useCourse;
